import type { Opportunity, VolunteerOrganization } from "@entities";
import {
  opportunityRepo,
  volunteerOrganizationRepo,
  volunteerOrgProjectAssignmentRepo,
} from "@repositories";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getRpcErrorMessage } from "@shared/frontend/functions/get-rpc-error-message.function";
import { allowsGroupVolunteer } from "@shared/helpers/opportunity-volunteer-mode";
import {
  volunteerOrgDisplayName,
  volunteerOrgIsActivatable,
} from "@shared/helpers/volunteer-organization";
import { linko } from "@shared/linko";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { primaryActionSx, quietButtonSx, surfaceCardSx } from "./volunteer-org-form-ui";

type MissionDraft = { id: string; title: string; size: string };

export function AssignVolunteerOrgMissionsDialog({
  open,
  onClose,
  onSaved,
  volunteerOrganization,
  opportunity,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  volunteerOrganization?: VolunteerOrganization | null;
  opportunity?: Pick<Opportunity, "id" | "title"> | null;
}) {
  const { currOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const [orgQuery, setOrgQuery] = useState("");
  const [orgOptions, setOrgOptions] = useState<VolunteerOrganization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<VolunteerOrganization | null>(
    volunteerOrganization ?? null,
  );
  const [assignmentId, setAssignmentId] = useState("");
  const [singleSize, setSingleSize] = useState("10");
  const [missionQuery, setMissionQuery] = useState("");
  const [missionOptions, setMissionOptions] = useState<Opportunity[]>([]);
  const [missions, setMissions] = useState<MissionDraft[]>(
    opportunity ? [{ id: opportunity.id, title: opportunity.title, size: "10" }] : [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedOrg(volunteerOrganization ?? null);
    setAssignmentId("");
    setSingleSize("10");
    setMissions(
      opportunity
        ? [{ id: opportunity.id, title: opportunity.title, size: "10" }]
        : [],
    );
    setError(null);
    setOrgQuery("");
    setMissionQuery("");
    setOrgOptions([]);
    setMissionOptions([]);
  }, [open, volunteerOrganization, opportunity]);

  const orgId = selectedOrg?.id ?? "";
  const { data: linkedGroups = [], refetch: refetchLinked } = useQuery({
    queryKey: ["volunteer-org-mission-groups", orgId],
    enabled: open && Boolean(orgId),
    queryFn: async () => {
      const response = await linko.group.queries.listVolunteerOrgMissionGroups(orgId);
      return response.data;
    },
  });

  const { data: loadedAssignments, isSuccess: assignmentsLoaded } = useQuery({
    queryKey: ["volunteer-org-unit-assignments", orgId],
    enabled: open && Boolean(orgId),
    queryFn: () =>
      volunteerOrgProjectAssignmentRepo.find({
        where: { volunteerOrganizationId: orgId },
        include: { project: true },
        orderBy: { createdAt: "desc" },
      }),
  });
  const assignments = assignmentsLoaded
    ? (loadedAssignments ?? [])
    : (selectedOrg?.assignments ?? []);

  const searchOrgs = async () => {
    const term = orgQuery.trim();
    const rows = await volunteerOrganizationRepo.find({
      where: term ? { name: { $contains: term } } : {},
      include: {
        parent: true,
        branches: true,
        assignments: { include: { project: true } },
      },
      orderBy: { name: "asc" },
      limit: 20,
    });
    setOrgOptions(rows.filter(volunteerOrgIsActivatable));
  };

  const searchMissions = async () => {
    if (!currOrganization?.id) return;
    const term = missionQuery.trim();
    const rows = await opportunityRepo.find({
      where: {
        isDeleted: false,
        isClosed: false,
        isOpportunityGroupParent: false,
        organization: { $id: currOrganization.id },
        ...(term ? { title: { $contains: term } } : {}),
      },
      orderBy: { startDate: "asc" },
      limit: 30,
    });
    setMissionOptions(rows.filter((row) => allowsGroupVolunteer(row.volunteerMode)));
  };

  const selectedAssignment = useMemo(
    () => assignments.find((row) => row.id === assignmentId) ?? null,
    [assignments, assignmentId],
  );

  const submit = async () => {
    if (!selectedOrg) {
      setError("בחרו ארגון מתנדבים");
      return;
    }
    const payloadMissions = opportunity
      ? [{ opportunityId: opportunity.id, size: Number(singleSize) }]
      : missions.map((row) => ({
          opportunityId: row.id,
          size: Number(row.size),
        }));
    if (payloadMissions.length === 0) {
      setError("בחרו לפחות משימה אחת");
      return;
    }
    if (payloadMissions.some((row) => !Number.isInteger(row.size) || row.size < 1)) {
      setError("גודל הקבוצה לכל משימה חייב להיות מספר שלם חיובי");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await linko.group.mutations.assignVolunteerOrgToMissions({
        volunteerOrganizationId: selectedOrg.id,
        projectAssignmentId: assignmentId || null,
        missions: payloadMissions,
      });
      await refetchLinked();
      await queryClient.invalidateQueries({
        queryKey: ["volunteer-org-mission-groups", selectedOrg.id],
      });
      onSaved?.();
      if (opportunity) onClose();
    } catch (err) {
      setError(getRpcErrorMessage(err) || "השיוך נכשל");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        שיוך למשימות
        <IconButton onClick={onClose} aria-label="סגירה" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 0.5 }}>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            לכל משימה נוצרת קבוצה עם הגודל שתבחרו. הקבוצה נשארת עם המאצ'ים, ולא מופיעה ברשימת הקבוצות הרגילה.
          </Typography>
          {opportunity ? (
            <Typography sx={{ fontWeight: 700 }}>{opportunity.title}</Typography>
          ) : null}
          {volunteerOrganization ? (
            <Typography sx={{ fontWeight: 700 }}>
              {volunteerOrgDisplayName(
                volunteerOrganization.name,
                volunteerOrganization.parent?.name,
              )}
            </Typography>
          ) : (
            <Stack gap={1}>
              <Stack direction="row" gap={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="חיפוש ארגון"
                  value={orgQuery}
                  onChange={(event) => setOrgQuery(event.target.value)}
                />
                <Button sx={quietButtonSx} onClick={() => void searchOrgs()}>
                  חפש
                </Button>
              </Stack>
              {orgOptions.map((org) => (
                <Button
                  key={org.id}
                  variant={selectedOrg?.id === org.id ? "contained" : "outlined"}
                  sx={{ ...quietButtonSx, justifyContent: "flex-start" }}
                  onClick={() => {
                    setSelectedOrg(org);
                    setAssignmentId("");
                  }}
                >
                  {volunteerOrgDisplayName(org.name, org.parent?.name)}
                </Button>
              ))}
            </Stack>
          )}
          {selectedOrg ? (
            <TextField
              select
              size="small"
              label="שיבוץ לפרויקט"
              value={assignmentId}
              onChange={(event) => setAssignmentId(event.target.value)}
              helperText={
                selectedAssignment
                  ? `${selectedAssignment.project?.name ?? "פרויקט"} · ${selectedAssignment.volunteerCount} בשיבוץ. הגודל במשימה לא מוגבל לזה.`
                  : "אפשר בלי שיבוץ. אם בוחרים שיבוץ, הקבוצה מקושרת אליו."
              }
            >
              <MenuItem value="">בלי שיבוץ</MenuItem>
              {assignments.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.project?.name ?? "פרויקט"} · {row.volunteerCount} · {row.address}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {opportunity ? (
            <TextField
              size="small"
              type="number"
              label="גודל הקבוצה למשימה"
              value={singleSize}
              onChange={(event) => setSingleSize(event.target.value)}
            />
          ) : (
            <Stack gap={1}>
              <Stack direction="row" gap={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="חיפוש משימה"
                  value={missionQuery}
                  onChange={(event) => setMissionQuery(event.target.value)}
                />
                <Button sx={quietButtonSx} onClick={() => void searchMissions()}>
                  חפש
                </Button>
              </Stack>
              {missionOptions.map((row) => (
                <Button
                  key={row.id}
                  variant="outlined"
                  sx={{ ...quietButtonSx, justifyContent: "flex-start" }}
                  onClick={() =>
                    setMissions((current) =>
                      current.some((item) => item.id === row.id)
                        ? current
                        : [...current, { id: row.id, title: row.title, size: "10" }],
                    )
                  }
                >
                  {row.title}
                </Button>
              ))}
              {missions.map((row) => (
                <Stack key={row.id} direction="row" gap={1} alignItems="center">
                  <Typography sx={{ flex: 1, fontSize: 14 }}>{row.title}</Typography>
                  <TextField
                    size="small"
                    type="number"
                    label="גודל"
                    value={row.size}
                    onChange={(event) =>
                      setMissions((current) =>
                        current.map((item) =>
                          item.id === row.id
                            ? { ...item, size: event.target.value }
                            : item,
                        ),
                      )
                    }
                    sx={{ width: 100 }}
                  />
                  <IconButton
                    aria-label="הסרת משימה"
                    onClick={() =>
                      setMissions((current) => current.filter((item) => item.id !== row.id))
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
          {linkedGroups.length > 0 ? (
            <Stack gap={1}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>קבוצות במשימות</Typography>
              {linkedGroups.map((group) => (
                <Stack
                  key={group.groupId}
                  gap={0.5}
                  sx={surfaceCardSx}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    {group.missions.map((mission) => mission.title).join(", ") || group.name}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    {group.size} מתנדבים
                    {group.projectName ? ` · ${group.projectName}` : ""}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button sx={quietButtonSx} onClick={onClose}>
          ביטול
        </Button>
        <Button
          variant="contained"
          sx={primaryActionSx}
          disabled={saving || !selectedOrg}
          onClick={() => void submit()}
        >
          שייך
        </Button>
      </DialogActions>
    </Dialog>
  );
}
